import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try { await login(email, password); navigate("/"); }
    catch (err) { setError(apiError(err.response?.data?.detail) || "Login failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img src="https://images.unsplash.com/photo-1600531529272-023c4b821f14?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#0047FF]/85" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-2.5">
            <div className="h-10 w-10 rounded-md bg-white/15 flex items-center justify-center font-heading font-extrabold text-lg">g</div>
            <span className="font-heading font-extrabold text-lg">goStops</span>
          </div>
          <div>
            <h2 className="font-heading text-4xl font-extrabold leading-tight">Franchise pipeline,<br />one source of truth.</h2>
            <p className="mt-4 text-white/70 max-w-md text-sm leading-relaxed">Capture every lead, promote to deals, and track each property from first contact to a live hostel — with a full activity trail.</p>
          </div>
          <div className="text-xs text-white/50">Internal CRM · goStops Business Development</div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 bg-white">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <h1 className="font-heading text-2xl font-extrabold text-gray-900">Sign in</h1>
          <p className="text-sm text-gray-500 mt-1 mb-8">Use the account provided by your admin.</p>

          <div className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1.5" data-testid="login-email" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-1.5" data-testid="login-password" />
            </div>
            {error && <div className="text-sm text-rose-600" aria-live="polite" data-testid="login-error">{error}</div>}
            <Button type="submit" className="w-full" disabled={busy} data-testid="login-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Sign in
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
